import { useState, useMemo } from 'react'
import { router, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { LoanBadge } from '@/lib/loanIdentifiers'
import type { LoanProps, BorrowerWithLoans } from '@/types/loan'

interface Props {
  borrowers_with_loans: BorrowerWithLoans[]
}

type Step = 'select-loan' | 'enter-amount' | 'confirm'

export default function QuickEntry({ borrowers_with_loans }: Props) {
  const [step, setStep] = useState<Step>('select-loan')
  const [selectedLoan, setSelectedLoan] = useState<LoanProps | null>(null)
  const [amount, setAmount] = useState('')
  const [search, setSearch] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Flatten all loans for easy selection
  const allLoans = useMemo(() => {
    const loans: (LoanProps & { _borrowerName: string })[] = []
    for (const b of borrowers_with_loans) {
      for (const l of b.active_loans) {
        loans.push({ ...l, _borrowerName: b.name })
      }
    }
    return loans
  }, [borrowers_with_loans])

  const filteredLoans = useMemo(() => {
    if (!search) return allLoans
    const q = search.toLowerCase()
    return allLoans.filter(
      (l) =>
        l._borrowerName.toLowerCase().includes(q) ||
        l.purpose?.toLowerCase().includes(q) ||
        String(l.loan_number).includes(q)
    )
  }, [allLoans, search])

  const expected = selectedLoan?.expected_next_payment ?? null

  const liveSplit = useMemo(() => {
    const amt = parseFloat(amount) || 0
    if (!expected || amt <= 0) return null
    const expInterest = expected.interest
    let interest: number, principal: number
    if (amt >= expInterest + expected.principal) {
      interest = expInterest
      principal = Math.round((amt - expInterest) * 100) / 100
    } else if (amt <= expInterest) {
      interest = amt
      principal = 0
    } else {
      interest = expInterest
      principal = Math.round((amt - expInterest) * 100) / 100
    }
    return { interest, principal }
  }, [amount, expected])

  function handleSelectLoan(loan: LoanProps & { _borrowerName: string }) {
    setSelectedLoan(loan)
    if (loan.expected_next_payment) {
      setAmount(String(loan.expected_next_payment.amount))
    } else {
      setAmount(String(loan.monthly_payment))
    }
    setStep('enter-amount')
  }

  function handlePayFull() {
    if (expected) setAmount(String(expected.amount))
  }

  function handleContinue() {
    if (!amount || parseFloat(amount) <= 0) return
    setStep('confirm')
  }

  function handleSubmit() {
    if (!selectedLoan) return
    setSubmitting(true)
    setError('')

    router.post(`/loans/${selectedLoan.id}/payments`, {
      payment: {
        amount: parseFloat(amount),
        date: new Date().toISOString().split('T')[0],
        note: null,
        late_fee: 0,
      },
    }, {
      onSuccess: () => {
        setSubmitting(false)
        router.visit('/payments')
      },
      onError: (errors) => {
        setSubmitting(false)
        const msgs = Object.values(errors).flat()
        setError(Array.isArray(msgs) ? msgs.join('. ') : 'Something went wrong.')
      },
      preserveScroll: true,
    })
  }

  function handleBack() {
    if (step === 'enter-amount') {
      setStep('select-loan')
      setSelectedLoan(null)
      setAmount('')
    } else if (step === 'confirm') {
      setStep('enter-amount')
    }
  }

  return (
    <AppLayout>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {step !== 'select-loan' && (
            <button
              onClick={handleBack}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">Quick Payment</h1>
            <p className="text-xs text-gray-500">
              {step === 'select-loan' && 'Step 1 of 3 — Select loan'}
              {step === 'enter-amount' && 'Step 2 of 3 — Enter amount'}
              {step === 'confirm' && 'Step 3 of 3 — Confirm'}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1.5 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                (step === 'select-loan' && s <= 1) ||
                (step === 'enter-amount' && s <= 2) ||
                (step === 'confirm' && s <= 3)
                  ? 'bg-emerald-500'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Select Loan */}
        {step === 'select-loan' && (
          <div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search borrowers..."
              className="w-full py-3 px-4 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-4"
              autoFocus
            />

            {filteredLoans.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-400">No active loans found</p>
                <Link href="/loans/new" className="text-sm text-emerald-600 hover:text-emerald-700 mt-2 inline-block">
                  Create a loan
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLoans.map((loan) => (
                  <button
                    key={loan.id}
                    onClick={() => handleSelectLoan(loan)}
                    className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-300 hover:shadow-sm transition-all active:scale-[0.98] min-h-[80px]"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-base font-semibold text-gray-900">{loan._borrowerName}</span>
                      <LoanBadge loanNumber={loan.loan_number} />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        Balance: <span className="font-mono font-medium text-gray-700">${loan.remaining_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </span>
                      {loan.expected_next_payment && (
                        <span className="text-emerald-600 font-mono font-medium">
                          ${loan.expected_next_payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Enter Amount */}
        {step === 'enter-amount' && selectedLoan && (
          <div>
            {/* Selected loan context */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
              <p className="text-sm font-medium text-gray-900">{selectedLoan.borrower_name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Balance: ${selectedLoan.remaining_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                {expected && ` — Payment #${expected.payment_number} due ${expected.due_date}`}
              </p>
            </div>

            {/* Amount input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl">$</span>
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full py-4 pl-10 pr-4 text-2xl font-mono font-bold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-center"
                  autoFocus
                />
              </div>
            </div>

            {/* Pay Full Amount shortcut */}
            {expected && (
              <button
                onClick={handlePayFull}
                className="w-full py-3 px-4 mb-4 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors active:scale-[0.98] min-h-[48px]"
              >
                Pay Full Amount — ${expected.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </button>
            )}

            {/* Live split preview */}
            {liveSplit && (
              <div className="flex gap-3 mb-6">
                <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <span className="text-emerald-600 text-xs font-medium">Principal</span>
                  <p className="font-bold text-emerald-700 font-mono mt-0.5">
                    ${liveSplit.principal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="flex-1 bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
                  <span className="text-indigo-600 text-xs font-medium">Interest</span>
                  <p className="font-bold text-indigo-700 font-mono mt-0.5">
                    ${liveSplit.interest.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={handleContinue}
              disabled={!amount || parseFloat(amount) <= 0}
              className="w-full py-4 text-base font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50 active:scale-[0.98] min-h-[56px]"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && selectedLoan && (
          <div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Payment Summary</h3>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Borrower</span>
                  <span className="font-medium text-gray-900">{selectedLoan.borrower_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Loan</span>
                  <LoanBadge loanNumber={selectedLoan.loan_number} />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-gray-900">{new Date().toLocaleDateString()}</span>
                </div>
                <div className="border-t border-gray-100 pt-3 flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Amount</span>
                  <span className="text-xl font-bold font-mono text-gray-900">
                    ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {liveSplit && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-4 text-sm">
                  <div className="flex-1 text-center">
                    <span className="text-gray-400 text-xs">Principal</span>
                    <p className="font-semibold text-emerald-600 font-mono">
                      ${liveSplit.principal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="flex-1 text-center">
                    <span className="text-gray-400 text-xs">Interest</span>
                    <p className="font-semibold text-indigo-500 font-mono">
                      ${liveSplit.interest.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 text-base font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50 active:scale-[0.98] min-h-[56px] flex items-center justify-center gap-2"
            >
              {submitting && (
                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {submitting ? 'Recording...' : 'Confirm Payment'}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
